import { useEffect, useState } from 'react';
import { getOrg } from '../../api/client';
import type { Organization, OrgMember } from '@heybesafe/shared';
import { useFamilyKeys } from '../../context/FamilyKeysContext';
import { useMemberNames, memberLabel } from '../../hooks/useMemberNames';

interface Props {
  orgId: string;
  /** Bump this (e.g. a counter) to force a re-fetch — used by OverviewTab so
   *  a Save/Clear in the neighboring MyFamilyNameCard is reflected here
   *  without a full page reload. See MyFamilyNameCard's `onSaved` prop. */
  refreshSignal?: number;
}

export function MemberRosterCard({ orgId, refreshSignal }: Props) {
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [org, setOrg] = useState<Organization | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    getOrg(orgId).then((r) => {
      if (!cancelled && r.ok) {
        setMembers(r.data.members);
        setOrg(r.data.org as Organization);
      }
    });
    return () => { cancelled = true; };
  }, [orgId, refreshSignal]);

  const keys = useFamilyKeys();
  const names = useMemberNames(orgId, members, org, keys);

  return (
    <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-4">
      <h2 className="text-xs uppercase tracking-wide text-hbs-text-mute mb-3">
        Members — {members.length}
      </h2>
      <ul className="space-y-2">
        {members.map((m) => (
          <li key={m.userId} className="flex items-center justify-between text-sm">
            <span className="min-w-0">
              <span className="block truncate text-hbs-text">{memberLabel(m, names)}</span>
              {names.get(m.userId)?.trim() && (
                <span className="block truncate text-xs text-hbs-text-mute">{m.email}</span>
              )}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              m.role === 'admin'
                ? 'bg-hbs-primary/10 text-hbs-primary'
                : 'bg-hbs-surface-alt text-hbs-text-mute'
            }`}>
              {m.role === 'admin' ? 'Admin' : 'Member'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
